import axios from "axios";
import { calculateVisibleFieldProgress } from "../utils/formVisibleProgress";

const API_URL = process.env.REACT_APP_API_URL;
const ATHLETE_ID = "6884aa884fb3247b61564ae5";

const FORM_IDS = {
    REGISTRATION: "68c7eefbd44999eedb541043",
    ACCREDITATION: "68e6505866f25479291b933f",
    TRAVEL: "68e781408e1384b2c283c3af",
    ACCOMMODATION: "68e784018e1384b2c283c3bb",
    GUEST: "68e785cb8e1384b2c283c3c5",
};

const FORM_MAPPING = {
    [FORM_IDS.REGISTRATION]: { segment: "registration" },
    [FORM_IDS.ACCREDITATION]: { segment: "accreditation" },
    [FORM_IDS.TRAVEL]: { segment: "travel" },
    [FORM_IDS.ACCOMMODATION]: { segment: "accommodation" },
    [FORM_IDS.GUEST]: { segment: "guest" },
};

let panelData = null;
const subscribers = new Set();

/* ========================================= */
/* FORM PROGRESS (visible UI fields only)    */
/* ========================================= */

export const calculateFormProgress = (form) => {
    const result = calculateVisibleFieldProgress(
        form?.data || {},
        form?.components || [],
        form || null
    );
    return {
        total: result.total,
        completed: result.filled,
        display: `${result.filled}/${result.total}`,
    };
};

/* ========================================= */
/* NOTIFY                                    */
/* ========================================= */

const notify = () => {
    if (!panelData) return;

    const total = Object.values(panelData).reduce(
        (sum, p) => sum + (p.total || 0),
        0
    );

    const completed = Object.values(panelData).reduce(
        (sum, p) => sum + (p.completed || 0),
        0
    );

    subscribers.forEach((cb) =>
        cb({
            ...panelData,
            total: { total, completed },
        })
    );
};

/* ========================================= */
/* SAVE PROGRESS                             */
/* ========================================= */

const lastPersistedProgress = new Map();

const progressUnchanged = (a, b) =>
  a &&
  b &&
  Number(a.total) === Number(b.total) &&
  Number(a.completed) === Number(b.completed);

/** Sidebar badges only — no API call (safe on every Form.io onChange). */
export const updateLocalPanelProgress = (segment, progress) => {
  // Allow live form progress before loadSavedProgress has run.
  if (!panelData) {
    panelData = {
      registration: { total: 0, completed: 0, display: "0/0" },
      accreditation: { total: 0, completed: 0, display: "0/0" },
      travel: { total: 0, completed: 0, display: "0/0" },
      accommodation: { total: 0, completed: 0, display: "0/0" },
      guest: { total: 0, completed: 0, display: "0/0" },
    };
  }

  const prev = panelData[segment];
  if (progressUnchanged(prev, progress)) return;

  panelData[segment] = {
    ...progress,
    display: progress.display ?? `${progress.completed}/${progress.total}`,
  };
  notify();
};

export const setFormProgressData = (participantId, segment, progress) => {
  updateLocalPanelProgress(segment, progress);

  const persistKey = `${participantId}:${segment}`;
  const last = lastPersistedProgress.get(persistKey);
  if (progressUnchanged(last, progress)) return;

  lastPersistedProgress.set(persistKey, {
    total: progress.total,
    completed: progress.completed,
  });

  axios
    .post(`${API_URL}/panel-progress/update`, {
      participantId,
      segment,
      total: progress.total,
      completed: progress.completed,
      display: progress.display ?? `${progress.completed}/${progress.total}`,
    })
    .catch((err) =>
      console.error(`Failed to save progress (${segment})`, err)
    );
};




export const updateProgressFromForm = (form, participantId) => {
  if (!form || !panelData) return;

  const entry = Object.entries(FORM_IDS).find(
    ([, id]) => id === form._id
  );
  if (!entry) return;

  const { segment } = FORM_MAPPING[entry[1]];

  const progress = calculateFormProgress(form);
  setFormProgressData(participantId, segment, progress);
};

/* ========================================= */
/* FORM LISTENER                             */
/* ========================================= */

export const attachFormListener = (form, participantId) => {
    if (!form) return;
    form.on("ready", () => updateProgressFromForm(form, participantId));
    form.on("change", () => updateProgressFromForm(form, participantId));
};

/* ========================================= */
/* LOAD SAVED PROGRESS (AUTHORITATIVE)       */
/* ========================================= */

/* ========================================= */
/* LOAD SAVED PROGRESS (AUTHORITATIVE)       */
/* ========================================= */

export const loadSavedProgress = async (participantId) => {
    try {
        const res = await axios.get(
            `${API_URL}/ems/participants/${participantId}`
        );

        const p = res.data.participant || res.data;
        const groupId = String(p.group_id?._id || p.group_id);
        const isAthlete = groupId === ATHLETE_ID;
        const prev = panelData || {};

        // Prefer fresher in-memory filled/total when DB row is still 0/stale.
        const pick = (segment, dbFallback) => {
            const db = p.progress_breakdown?.[segment] || dbFallback;
            const local = prev[segment];
            if (
                local &&
                Number(local.total) > 0 &&
                Number(local.completed) > Number(db?.completed || 0)
            ) {
                return {
                    ...local,
                    display: local.display ?? `${local.completed}/${local.total}`,
                };
            }
            return {
                total: db?.total || 0,
                completed: db?.completed || 0,
                display: db?.display ?? `${db?.completed || 0}/${db?.total || 0}`,
            };
        };

        const zero = { total: 0, completed: 0, display: "0/0" };

        panelData = {
            registration: pick("registration", zero),
            accreditation: pick("accreditation", zero),
            guest: pick("guest", zero),
            ...(isAthlete
                ? {}
                : {
                      travel: pick("travel", zero),
                      accommodation: pick("accommodation", zero),
                  }),
        };

        notify();

        return isAthlete;
    } catch (err) {
        console.error("Failed to load saved progress", err);
        return false;
    }
};


/* ========================================= */
/* SUBSCRIBE                                 */
/* ========================================= */

export const subscribeToPanelData = (cb) => {
    subscribers.add(cb);
    if (panelData) notify();
    return () => subscribers.delete(cb);
};
